import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Search, Plus, Phone, Mail, Eye, Loader2, RotateCcw, AlertCircle, TrendingUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AddCustomerModal } from '@/components/customers/AddCustomerModal';
import { useAuth } from '@/hooks/use-auth';
import type { Tables } from '@/integrations/supabase/types';
import { CustomerTag } from '@/components/ui/customer-tag';
import { AUTO_TAG_CONFIG, MANUAL_TAG_CONFIG, TAG_CATEGORIES } from '@/lib/customer-tags';
import { cn } from '@/lib/utils';
import { customerService } from '@/lib/packages';

type CustomerRow = Tables<'customers'>;

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  status: 'active' | 'inside' | 'vip';
  lastVisit: string;
  totalSpent: string;
  color: CustomerRow['color'];
  tags_manual: CustomerRow['tags_manual'];
  tags_auto: CustomerRow['tags_auto'];
  visit_count_total: number;
  last_seen_at: string | null;
}

const statusConfig = {
  active: { label: 'Aktif', color: 'bg-success/20 text-success' },
  inside: { label: 'İçeride', color: 'bg-accent/20 text-accent' },
  vip: { label: 'VIP', color: 'bg-warning/20 text-warning' },
};

const colorConfig = {
  mavi: { label: 'Mavi', bgClass: 'bg-blue-500/20' },
  kirmizi: { label: 'Kırmızı', bgClass: 'bg-red-500/20' },
  yesil: { label: 'Yeşil', bgClass: 'bg-green-500/20' },
  sari: { label: 'Sarı', bgClass: 'bg-yellow-500/20' },
  siyah: { label: 'Siyah', bgClass: 'bg-gray-500/20' },
  beyaz: { label: 'Beyaz', bgClass: 'bg-slate-100/80' },
  gold: { label: 'Altın', bgClass: 'bg-yellow-600/20' },
};

const Customers = () => {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);

  const formatLastVisit = (lastSeenAt: string | null) => {
    if (!lastSeenAt) return 'Hiç ziyaret etmedi';
    
    const lastSeen = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays === 1) return '1 gün önce';
    if (diffDays < 7) return `${diffDays} gün önce`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
    return `${Math.floor(diffDays / 30)} ay önce`;
  };

  const determineStatus = (customerRow: CustomerRow, isCurrentlyInside: boolean): 'active' | 'inside' | 'vip' => {
    if (isCurrentlyInside) return 'inside';
    if (customerRow.tags_manual?.includes('vip')) return 'vip';
    return 'active';
  };

  const fetchCustomers = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Use paginated getAllCustomers to fetch all customers (handles >1000 rows)
      const customersData = await customerService.getAllCustomers(companyId);

      if (customersData) {
        const customerIds = customersData.map(c => c.id);
        
        // Create a map of customer_id -> total spent
        const totalSpentMap = new Map<string, number>();
        
        // Create a set of customer IDs for quick lookup
        const customerIdSet = new Set(customerIds);
        
        // Fetch all completed sales for this company
        // This approach is more reliable than using .in() which has limitations
        let allSales: any[] = [];
        let hasMore = true;
        // Fetch converted onhold records to identify sales linked to onhold conversions
        const { data: convertedOnHolds } = await supabase
          .from('on_holds')
          .select('linked_sale_id')
          .eq('company_id', companyId)
          .eq('status', 'converted')
          .not('linked_sale_id', 'is', null);
        
        const onHoldLinkedSaleIds = new Set(
          (convertedOnHolds || [])
            .map((onHold: any) => onHold.linked_sale_id)
            .filter(Boolean)
        );

        let page = 0;
        const pageSize = 1000;
        
        while (hasMore) {
          const { data: salesPage, error: salesError } = await supabase
            .from('sales')
            .select('id, customer_id, total_net')
            .eq('company_id', companyId)
            .eq('status', 'completed')
            .not('customer_id', 'is', null)
            .range(page * pageSize, (page + 1) * pageSize - 1);
          
          if (salesError) {
            console.error('Error fetching sales for customers:', salesError);
            break;
          }
          
          if (salesPage && salesPage.length > 0) {
            allSales = [...allSales, ...salesPage];
            page++;
            hasMore = salesPage.length === pageSize;
          } else {
            hasMore = false;
          }
        }
        
        // Filter out 0 TL sales that are linked to onhold conversions
        const filteredSales = allSales.filter((sale) => {
          const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
          const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
          return !(isZeroAmount && isLinkedToOnHold);
        });
        
        // Aggregate total spent per customer (only for customers we care about)
        if (filteredSales.length > 0) {
          filteredSales.forEach(sale => {
            if (sale.customer_id && customerIdSet.has(sale.customer_id) && sale.total_net) {
              const currentTotal = totalSpentMap.get(sale.customer_id) || 0;
              totalSpentMap.set(sale.customer_id, currentTotal + (sale.total_net || 0));
            }
          });
        }
        
        // Debug logging
        console.log(`Fetched ${allSales.length} sales, aggregated for ${totalSpentMap.size} customers`);
        if (totalSpentMap.size > 0) {
          const sample = Array.from(totalSpentMap.entries()).slice(0, 3);
          console.log('Sample total spent:', sample);
        }
        
        // Fetch all active visits for this company using pagination
        // This avoids the .in() query limitation when there are many customers
        let allActiveVisits: any[] = [];
        let hasMoreVisits = true;
        let visitPage = 0;
        const visitPageSize = 1000;
        
        while (hasMoreVisits) {
          const { data: visitsPage, error: visitsError } = await supabase
            .from('visits')
            .select('customer_id, id')
            .eq('company_id', companyId)
            .eq('status', 'PENDING_LABEL')
            .not('customer_id', 'is', null)
            .range(visitPage * visitPageSize, (visitPage + 1) * visitPageSize - 1);
          
          if (visitsError) {
            console.error('Error fetching active visits:', visitsError);
            hasMoreVisits = false;
            break;
          }
          
          if (visitsPage && visitsPage.length > 0) {
            allActiveVisits = [...allActiveVisits, ...visitsPage];
            visitPage++;
            hasMoreVisits = visitsPage.length === visitPageSize;
          } else {
            hasMoreVisits = false;
          }
        }
        
        // Create a set of customer IDs that are currently inside
        // Only include customers that are in our customerIdSet
        const insideCustomerIds = new Set<string>();
        allActiveVisits.forEach(visit => {
          if (visit.customer_id && customerIdSet.has(visit.customer_id)) {
            insideCustomerIds.add(visit.customer_id);
          }
        });
        
        console.log(`Fetched ${allActiveVisits.length} active visits, ${insideCustomerIds.size} customers currently inside`);
        
        // Process customers (no async operations needed now)
        const processedCustomers: Customer[] = customersData.map((customerRow) => {
          const totalSpent = totalSpentMap.get(customerRow.id) || 0;
          const isInside = insideCustomerIds.has(customerRow.id);
          const name = `${customerRow.first_name || ''} ${customerRow.last_name || ''}`.trim() || 'İsimsiz Müşteri';
          
          return {
            id: customerRow.id,
            name,
            email: customerRow.email || '',
            phone: customerRow.phone || '',
            status: determineStatus(customerRow, isInside),
            lastVisit: isInside ? 'Şu anda içeride' : formatLastVisit(customerRow.last_seen_at),
            totalSpent: `₺${totalSpent.toLocaleString('tr-TR')}`,
            color: customerRow.color,
            tags_manual: customerRow.tags_manual,
            tags_auto: customerRow.tags_auto,
            visit_count_total: customerRow.visit_count_total,
            last_seen_at: customerRow.last_seen_at
          };
        });

        setCustomers(processedCustomers);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Müşteriler yüklenirken hata oluştu');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      void fetchCustomers();
    }
  }, [companyId]);

  const handleCustomerClick = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const totalCustomers = customers.length;
    if (totalCustomers === 0) {
      return {
        returned: { count: 0, percentage: 0 },
        atRisk: { count: 0, percentage: 0 },
        newCustomers: { count: 0, percentage: 0 },
        frequent: { count: 0, percentage: 0 },
      };
    }

    const returnedTags = ['geri_donen', 'geri_kazanildi'];
    const atRiskTags = ['kayip_riski', 'cok_kayip_riski'];
    const newCustomerTags = ['yeni_musteri'];
    const frequentTags = ['sik_gelen', 'cok_sik_gelen', 'haftalik_aktif'];

    const returned = customers.filter(c => 
      c.tags_auto?.some(tag => returnedTags.includes(tag))
    ).length;
    
    const atRisk = customers.filter(c => 
      c.tags_auto?.some(tag => atRiskTags.includes(tag))
    ).length;

    const newCustomers = customers.filter(c => 
      c.tags_auto?.some(tag => newCustomerTags.includes(tag))
    ).length;

    const frequent = customers.filter(c => 
      c.tags_auto?.some(tag => frequentTags.includes(tag))
    ).length;

    return {
      returned: { 
        count: returned, 
        percentage: Math.round((returned / totalCustomers) * 100) 
      },
      atRisk: { 
        count: atRisk, 
        percentage: Math.round((atRisk / totalCustomers) * 100) 
      },
      newCustomers: { 
        count: newCustomers, 
        percentage: Math.round((newCustomers / totalCustomers) * 100) 
      },
      frequent: { 
        count: frequent, 
        percentage: Math.round((frequent / totalCustomers) * 100) 
      },
    };
  }, [customers]);

  // Get all unique auto tags with counts
  const autoTagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    customers.forEach(customer => {
      customer.tags_auto?.forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    });
    return counts;
  }, [customers]);

  // Get all unique manual tags with counts
  const manualTagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    customers.forEach(customer => {
      customer.tags_manual?.forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    });
    return counts;
  }, [customers]);

  // Get all unique colors with counts
  const colorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    customers.forEach(customer => {
      if (customer.color) {
        counts[customer.color] = (counts[customer.color] || 0) + 1;
      }
    });
    return counts;
  }, [customers]);

  // Get all available auto tags sorted by count
  const availableAutoTags = useMemo(() => {
    return Object.keys(AUTO_TAG_CONFIG).map(tag => ({
      tag,
      count: autoTagCounts[tag] || 0,
      config: AUTO_TAG_CONFIG[tag as keyof typeof AUTO_TAG_CONFIG],
      isManual: false
    })).filter(t => t.count > 0).sort((a, b) => b.count - a.count);
  }, [autoTagCounts]);

  // Get all available manual tags sorted by count
  const availableManualTags = useMemo(() => {
    return Object.keys(MANUAL_TAG_CONFIG).map(tag => ({
      tag,
      count: manualTagCounts[tag] || 0,
      config: MANUAL_TAG_CONFIG[tag as keyof typeof MANUAL_TAG_CONFIG],
      isManual: true
    })).filter(t => t.count > 0).sort((a, b) => b.count - a.count);
  }, [manualTagCounts]);

  // Get all available colors sorted by count
  const availableColors = useMemo(() => {
    return Object.entries(colorConfig).map(([color, config]) => ({
      color,
      count: colorCounts[color] || 0,
      config
    })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);
  }, [colorCounts]);

  // Filter customers by selected tags, colors, and search term
  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    // DEBUG: trace filter pipeline
    console.log('[Customers filter] START', {
      searchTerm,
      selectedTags,
      selectedColors,
      totalCustomers: customers.length,
      uniqueCustomerIds: new Set(customers.map(c => c.id)).size,
      hasDuplicates: customers.length !== new Set(customers.map(c => c.id)).size,
    });

    // Filter by selected tags (customer must have ALL selected tags)
    // Respect tag source: manual tags must match manual list, auto tags must match auto list.
    if (selectedTags.length > 0) {
      filtered = filtered.filter(customer => 
        selectedTags.every(tag => {
          const isManualTag = tag in MANUAL_TAG_CONFIG;
          const isAutoTag = tag in AUTO_TAG_CONFIG;

          if (isManualTag) {
            return customer.tags_manual?.includes(tag) ?? false;
          }
          if (isAutoTag) {
            return customer.tags_auto?.includes(tag) ?? false;
          }

          // Fallback for unknown tags in legacy data.
          return (customer.tags_auto?.includes(tag) ?? false) || (customer.tags_manual?.includes(tag) ?? false);
        })
      );
    }

    // Filter by selected colors (customer must have ONE of the selected colors)
    if (selectedColors.length > 0) {
      filtered = filtered.filter(customer => 
        customer.color && selectedColors.includes(customer.color)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const beforeSearch = filtered.length;
      filtered = filtered.filter(customer => {
        const nameMatch = customer.name.toLowerCase().includes(term);
        const emailMatch = customer.email?.toLowerCase().includes(term) ?? false;
        const phoneMatch = customer.phone?.includes(searchTerm) ?? false;
        const passes = nameMatch || emailMatch || phoneMatch;
        if (passes) {
          console.log('[Customers filter] MATCH', {
            id: customer.id,
            name: customer.name,
            match: { nameMatch, emailMatch, phoneMatch },
          });
        }
        return passes;
      });

      console.log('[Customers filter] SEARCH', {
        term,
        searchTerm,
        beforeSearch,
        afterSearch: filtered.length,
        matchedNames: filtered.map(c => c.name),
      });

      // Sort by relevance: exact/starts-with matches first, then contains; secondary sort by last_seen_at
      if (term && filtered.length > 0) {
        const score = (c: Customer) => {
          const name = c.name.toLowerCase();
          const email = (c.email || '').toLowerCase();
          const phone = c.phone || '';
          if (name === term || email === term) return 100; // exact match
          if (name.startsWith(term) || email.startsWith(term)) return 80;
          if (phone === searchTerm || phone.endsWith(searchTerm)) return 75;
          if (name.includes(term) || email.includes(term)) return 50;
          if (phone.includes(searchTerm)) return 40;
          return 0;
        };
        filtered = [...filtered].sort((a, b) => {
          const diff = score(b) - score(a);
          if (diff !== 0) return diff;
          // Same relevance: most recently seen first
          const aDate = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
          const bDate = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
          return bDate - aDate;
        });

        console.log('[Customers filter] SORTED', {
          order: filtered.map((c, i) => ({ i, name: c.name, score: score(c), last_seen_at: c.last_seen_at })),
        });
      }
    }

    console.log('[Customers filter] FINAL', {
      filteredCount: filtered.length,
      first5: filtered.slice(0, 5).map(c => c.name),
      last5: filtered.slice(-5).map(c => c.name),
    });

    return filtered;
  }, [customers, selectedTags, selectedColors, searchTerm]);

  // DEBUG: log what will be rendered when filtered list changes (must be after filteredCustomers)
  useEffect(() => {
    if (searchTerm || selectedTags.length > 0 || selectedColors.length > 0) {
      console.log('[Customers render] filteredCustomers changed', {
        searchTerm,
        count: filteredCustomers.length,
        names: filteredCustomers.map(c => c.name),
      });
    }
  }, [filteredCustomers, searchTerm, selectedTags, selectedColors]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleColorToggle = (color: string) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const clearTagFilters = () => {
    setSelectedTags([]);
    setSelectedColors([]);
  };

  // Force list subtree remount when filter criteria changes to avoid stale rows.
  const listRenderKey = `${searchTerm.trim().toLowerCase()}|${selectedTags.slice().sort().join(',')}|${selectedColors.slice().sort().join(',')}`;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-h1 font-bold">Müşteriler</h1>
              <p className="text-sm sm:text-base md:text-body text-muted-foreground">Tüm müşteri bilgileri</p>
            </div>
          </div>
          
          <Button 
            className="gap-2 w-full sm:w-auto"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="w-4 h-4" />
            <span className="sm:inline">Yeni Müşteri</span>
          </Button>
        </div>

        {/* Customer Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Geri Dönen</p>
                  <h3 className="text-xl sm:text-2xl font-bold">
                    {metrics.returned.count} <span className="text-sm font-normal text-muted-foreground">({metrics.returned.percentage}%)</span>
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Kayıp Riski</p>
                  <h3 className="text-xl sm:text-2xl font-bold">
                    {metrics.atRisk.count} <span className="text-sm font-normal text-muted-foreground">({metrics.atRisk.percentage}%)</span>
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Yeni Müşteriler</p>
                  <h3 className="text-xl sm:text-2xl font-bold">
                    {metrics.newCustomers.count} <span className="text-sm font-normal text-muted-foreground">({metrics.newCustomers.percentage}%)</span>
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Sık Gelen</p>
                  <h3 className="text-xl sm:text-2xl font-bold">
                    {metrics.frequent.count} <span className="text-sm font-normal text-muted-foreground">({metrics.frequent.percentage}%)</span>
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tag and Color Filters */}
        <Card className="glass-panel">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filtreler</CardTitle>
              {(selectedTags.length > 0 || selectedColors.length > 0) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearTagFilters}
                  className="text-xs"
                >
                  <X className="w-3 h-3 mr-1" />
                  Temizle ({selectedTags.length + selectedColors.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Selected Filters */}
              {(selectedTags.length > 0 || selectedColors.length > 0) && (
                <div className="pb-3 border-b">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Seçili Filtreler:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map(tag => {
                      const autoConfig = AUTO_TAG_CONFIG[tag as keyof typeof AUTO_TAG_CONFIG];
                      const manualConfig = MANUAL_TAG_CONFIG[tag as keyof typeof MANUAL_TAG_CONFIG];
                      const config = autoConfig || manualConfig;
                      return (
                        <Badge
                          key={`tag-${tag}`}
                          variant="outline"
                          className={cn(
                            "cursor-pointer hover:opacity-80 transition-all",
                            config?.color || 'bg-slate-50 text-slate-700 border-slate-100'
                          )}
                          onClick={() => handleTagToggle(tag)}
                        >
                          {config?.icon && <config.icon className="w-3 h-3 mr-1" />}
                          {config?.label || tag}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      );
                    })}
                    {selectedColors.map(color => {
                      const config = colorConfig[color as keyof typeof colorConfig];
                      return (
                        <Badge
                          key={`color-${color}`}
                          variant="outline"
                          className={cn(
                            "cursor-pointer hover:opacity-80 transition-all",
                            config?.bgClass || 'bg-slate-50 text-slate-700 border-slate-100'
                          )}
                          onClick={() => handleColorToggle(color)}
                        >
                          <div className={cn("w-3 h-3 rounded-full mr-1.5", config?.bgClass)} />
                          {config?.label || color}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Color Filters */}
              {availableColors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Renkler
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map(({ color, count, config }) => {
                      const isSelected = selectedColors.includes(color);
                      return (
                        <Badge
                          key={color}
                          variant="outline"
                          className={cn(
                            "cursor-pointer transition-all hover:scale-105 flex items-center gap-1.5",
                            isSelected 
                              ? "ring-2 ring-primary ring-offset-2" 
                              : "hover:opacity-80",
                            config?.bgClass || 'bg-slate-50 text-slate-700 border-slate-100'
                          )}
                          onClick={() => handleColorToggle(color)}
                        >
                          <div className={cn("w-3 h-3 rounded-full", config?.bgClass)} />
                          {config?.label || color}
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/10 text-xs font-semibold">
                            {count}
                          </span>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual Tags */}
              {availableManualTags.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Manuel Etiketler
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableManualTags.map(({ tag, count, config }) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <Badge
                          key={`manual-${tag}`}
                          variant="outline"
                          className={cn(
                            "cursor-pointer transition-all hover:scale-105",
                            isSelected 
                              ? "ring-2 ring-primary ring-offset-2" 
                              : "hover:opacity-80",
                            config?.color || 'bg-slate-50 text-slate-700 border-slate-100'
                          )}
                          onClick={() => handleTagToggle(tag)}
                        >
                          {config?.icon && <config.icon className="w-3 h-3 mr-1" />}
                          {config?.label || tag}
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-black/10 text-xs font-semibold">
                            {count}
                          </span>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Auto Tags by Category */}
              {Object.entries(TAG_CATEGORIES).map(([category, categoryLabel]) => {
                const categoryTags = availableAutoTags.filter(t => t.config?.category === category);
                if (categoryTags.length === 0) return null;

                return (
                  <div key={category} className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {categoryLabel}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {categoryTags.map(({ tag, count, config }) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <Badge
                            key={`auto-${tag}`}
                            variant="outline"
                            className={cn(
                              "cursor-pointer transition-all hover:scale-105",
                              isSelected 
                                ? "ring-2 ring-primary ring-offset-2" 
                                : "hover:opacity-80",
                              config?.color || 'bg-slate-50 text-slate-700 border-slate-100'
                            )}
                            onClick={() => handleTagToggle(tag)}
                          >
                            {config?.icon && <config.icon className="w-3 h-3 mr-1" />}
                            {config?.label || tag}
                            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-black/10 text-xs font-semibold">
                              {count}
                            </span>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="glass-panel p-4 sm:p-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Müşteri ara..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="glass-panel p-4 sm:p-6 border-red-500/20 bg-red-500/10">
            <p className="text-red-600 text-center">{error}</p>
            <div className="flex justify-center mt-3">
              <Button onClick={fetchCustomers} variant="outline" size="sm">
                Tekrar Dene
              </Button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Müşteriler yükleniyor...</span>
            </div>
          </div>
        )}

        {/* Customers List */}
        {!loading && !error && (
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                Müşteriler {(filteredCustomers.length !== customers.length || selectedTags.length > 0 || selectedColors.length > 0) && (
                  <span className="text-muted-foreground font-normal">
                    ({filteredCustomers.length} / {customers.length})
                  </span>
                )}
              </h2>
            </div>
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchTerm || selectedTags.length > 0 || selectedColors.length > 0
                    ? 'Filtre sonucu bulunamadı' 
                    : 'Henüz müşteri yok'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || selectedTags.length > 0 || selectedColors.length > 0
                    ? 'Farklı filtreler veya arama terimleri deneyin'
                    : 'İlk müşterinizi ekleyerek başlayın'
                  }
                </p>
                {!searchTerm && selectedTags.length === 0 && selectedColors.length === 0 && (
                  <Button onClick={() => setIsAddModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Yeni Müşteri Ekle
                  </Button>
                )}
                {(searchTerm || selectedTags.length > 0 || selectedColors.length > 0) && (
                  <Button variant="outline" onClick={() => { setSearchTerm(''); setSelectedTags([]); setSelectedColors([]); }}>
                    Filtreleri Temizle
                  </Button>
                )}
              </div>
            ) : (
              <div key={listRenderKey} className="space-y-3 sm:space-y-4">
                {console.log('[Customers table] Names displayed in table:', filteredCustomers.map((c, i) => `${i + 1}. ${c.name}`))}
                {filteredCustomers.map((customer, index) => (
                  <div
                    key={`${customer.id}-${index}`}
                    onClick={() => handleCustomerClick(customer.id)}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 hover:bg-white/8 transition-all duration-200 hover-lift cursor-pointer"
                  >
                    <Avatar className="w-12 h-12 sm:w-12 sm:h-12 border-2 border-white/20 flex-shrink-0">
                      <AvatarImage src={customer.avatar} alt={customer.name} />
                      <AvatarFallback className="bg-accent/20 text-accent font-semibold">
                        {customer.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2 sm:mb-1">
                        <h3 className="font-semibold truncate">{customer.name}</h3>
                        <Badge 
                          variant="secondary" 
                          className={`${statusConfig[customer.status].color} text-xs`}
                        >
                          {statusConfig[customer.status].label}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-caption text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{customer.phone}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2 text-xs sm:text-caption">
                        <span className="truncate">Son: {customer.lastVisit}</span>
                        <span className="font-semibold font-tabular">
                          {customer.totalSpent}
                        </span>
                      </div>
                      
                      {/* Customer Tags */}
                      {(customer.tags_auto && customer.tags_auto.length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {customer.tags_auto.slice(0, 5).map(tag => (
                            <CustomerTag
                              key={tag}
                              tag={tag}
                              isManual={false}
                              showIcon={true}
                              showTooltip={true}
                              size="sm"
                            />
                          ))}
                          {customer.tags_auto.length > 5 && (
                            <Badge variant="outline" className="text-xs px-2 py-1 h-5 bg-muted text-muted-foreground border-muted">
                              +{customer.tags_auto.length - 5}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button variant="ghost" size="sm" className="w-full sm:w-auto">
                        <Eye className="w-4 h-4" />
                        <span className="sm:ml-1">Görüntüle</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add Customer Modal */}
        <AddCustomerModal 
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onCustomerAdded={fetchCustomers}
        />
      </div>
    </Layout>
  );
};

export default Customers;